import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { ScrollToHash } from "@/components/routing/ScrollToHash";
import { CookieConsent } from "@/components/common/CookieConsent";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { FuryBot } from "@/components/chat/FuryBot";
import { initializeFirmData } from "@/utils/firmDataService";
import { initFirmStore } from "@/utils/firmStore";
import "./App.css";

// Critical path - load immediately
import HomePage from "@/pages/HomePage";

// Lazy load non-critical pages
const FirmProfilePage = lazy(() => import("@/pages/FirmProfilePage"));
const QuizPage = lazy(() => import("@/pages/QuizPage"));
const MethodologyPage = lazy(() => import("@/pages/MethodologyPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const QuickStartPage = lazy(() => import("@/pages/QuickStartPage"));
const ProfitTrackerPage = lazy(() => import("@/pages/ProfitTrackerPage"));
const TraderDashboard = lazy(() => import("@/pages/TraderDashboard"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const CommandCenterPage = lazy(() => import("@/pages/CommandCenterPage"));
const TraderDnaPage = lazy(() => import("@/pages/TraderDnaPage"));
const AdminSeoPage = lazy(() => import("@/pages/AdminSeoPage"));
const MonitorDashboard = lazy(() => import("@/pages/admin/MonitorDashboard"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));
const DisclosuresPage = lazy(() => import("@/pages/DisclosuresPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const ToolsPage = lazy(() => import("@/pages/ToolsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const AuthCallback = lazy(() => import("@/components/auth/AuthCallback"));

// SEO Tier 1 Pages
const ComparisonPage = lazy(() => import("@/pages/seo/ComparisonPage"));
const BestFirmsPage = lazy(() => import("@/pages/seo/BestFirmsPage"));
const EasiestToPassPage = lazy(() => import("@/pages/seo/EasiestToPassPage"));
const CheapestFirmsPage = lazy(() => import("@/pages/seo/CheapestFirmsPage"));

const FastestPayoutPage = lazy(() => import("@/pages/seo/FastestPayoutPage"));
const BeginnersPage = lazy(() => import("@/pages/seo/BeginnersPage"));

// Programmatic SEO Pages
const FilteredComparisonPage = lazy(() => import("@/pages/seo/FilteredComparisonPage"));
const DrawdownTypesPage = lazy(() => import("@/pages/seo/DrawdownTypesPage"));
const AlternativesPage = lazy(() => import("@/pages/seo/AlternativesPage"));
const BestFirmsUnder100Page = lazy(() => import("@/pages/seo/BestFirmsUnder100Page"));
const PropFirmsWithoutConsistencyPage = lazy(() => import("@/pages/seo/PropFirmsWithoutConsistencyPage"));
const NoDailyLossPage = lazy(() => import("@/pages/seo/NoDailyLossPage"));
const NoTimeLimitPage = lazy(() => import("@/pages/seo/NoTimeLimitPage"));
const WithoutEvaluationPage = lazy(() => import("@/pages/seo/WithoutEvaluationPage"));
const InstantFundingPage = lazy(() => import("@/pages/seo/InstantFundingPage"));
const BestFirmsUnder200Page = lazy(() => import("@/pages/seo/BestFirmsUnder200Page"));
const NoActivationFeePage = lazy(() => import("@/pages/seo/NoActivationFeePage"));
const FastPayoutsPage = lazy(() => import("@/pages/seo/FastPayoutsPage"));
const TopstepVsBulenoxPage = lazy(() => import("@/pages/seo/TopstepVsBulenoxPage"));
const TopstepVsApexPage = lazy(() => import("@/pages/seo/TopstepVsApexPage"));
const BulenoxVsTradedayPage = lazy(() => import("@/pages/seo/BulenoxVsTradedayPage"));
const E8MarketsVsBlueGuardianPage = lazy(() => import("@/pages/seo/E8MarketsVsBlueGuardianPage"));
const TopstepVsApexVsBulenoxPage = lazy(() => import("@/pages/seo/TopstepVsApexVsBulenoxPage"));
const HighProfitSplitPage = lazy(() => import("@/pages/seo/HighProfitSplitPage"));
const LowDrawdownPage = lazy(() => import("@/pages/seo/LowDrawdownPage"));
const SmallAccountsPage = lazy(() => import("@/pages/seo/SmallAccountsPage"));
const DailyPayoutsPage = lazy(() => import("@/pages/seo/DailyPayoutsPage"));
const ScalpingPage = lazy(() => import("@/pages/seo/ScalpingPage"));
const DrawdownCalculatorPage = lazy(() => import("@/pages/seo/DrawdownCalculatorPage"));
const ConsistencyCalculatorPage = lazy(() => import("@/pages/seo/ConsistencyCalculatorPage"));
const DoNotTradeGame = lazy(() => import("@/pages/DoNotTradeGame"));
const AskPage = lazy(() => import("@/pages/seo/AskPage"));
const AskHubPage = lazy(() => import("@/pages/seo/AskHubPage"));
const ReviewPage = lazy(() => import("@/pages/seo/ReviewPage"));
const DynamicDiscountPage = lazy(() => import("@/pages/seo/discounts/DynamicDiscountPage"));
const DynamicComparisonPage = lazy(() => import("@/pages/seo/DynamicComparisonPage"));
const ComparisonHubPage = lazy(() => import("@/pages/seo/ComparisonHubPage"));
const PropFirmRulesPage = lazy(() => import("@/pages/seo/PropFirmRulesPage"));
const FirmDiscountsPage = lazy(() => import("@/pages/seo/FirmDiscountsPage"));
const BestPropFirms2026Page = lazy(() => import("@/pages/seo/BestPropFirms2026Page"));
const BestForNQPage = lazy(() => import("@/pages/seo/BestForNQPage"));
const BestForESPage = lazy(() => import("@/pages/seo/BestForESPage"));
const GetFundedFastPage = lazy(() => import("@/pages/seo/GetFundedFastPage"));
const PassingEvaluationsGuidePage = lazy(() => import("@/pages/seo/PassingEvaluationsGuidePage"));
const PropFirmTaxGuidePage = lazy(() => import("@/pages/seo/PropFirmTaxGuidePage"));
const ScalingPlansPage = lazy(() => import("@/pages/seo/ScalingPlansPage"));
const PayoutMethodsPage = lazy(() => import("@/pages/seo/PayoutMethodsPage"));
const CommunityPulsePage = lazy(() => import("@/pages/CommunityPulsePage"));
const PropFirmGuidesPage = lazy(() => import("@/pages/seo/PropFirmGuidesPage"));
const SitemapPage = lazy(() => import("@/pages/seo/SitemapPage"));
const PayoutStatusPage = lazy(() => import("@/pages/seo/PayoutStatusPage"));
const CostCalculatorPage = lazy(() => import("@/pages/seo/CostCalculatorPage"));
const ProfitCalculatorPage = lazy(() => import("@/pages/seo/ProfitCalculatorPage"));

// AlertWidget removed from UI (FuryBot owns bottom-right). Backend alerts logic preserved.

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      <span className="text-slate-500 text-sm">Loading...</span>
    </div>
  </div>
);

// Initialize firm data from API on app load
function useInitializeFirmData() {
  useEffect(() => {
    initializeFirmData();
    initFirmStore();
  }, []);
}

// Wrapper that checks for session_id hash SYNCHRONOUSLY during render
// This prevents race conditions with Google OAuth callback
const AppRouter = () => {
  const location = useLocation();
  
  // Initialize firm branding data from API
  useInitializeFirmData();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <>
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      <ScrollToTop />
      <ScrollToHash />
      <CookieConsent />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/firms/:slug" element={<FirmProfilePage />} />
          <Route path="/how-we-rank-prop-firms" element={<MethodologyPage />} />
          <Route path="/methodology" element={<Navigate to="/how-we-rank-prop-firms" replace />} />
          <Route path="/legal" element={<DisclosuresPage />} />
          <Route path="/terms" element={<LegalPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/disclosures" element={<DisclosuresPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/quickstart" element={<QuickStartPage />} />
          {/* ── User app (auth-gated via <RequireAuth>; redirects to /login or /signup) ── */}
          <Route path="/profit-tracker" element={<RequireAuth redirectTo="/signup"><ProfitTrackerPage /></RequireAuth>} />
          <Route path="/dashboard" element={<RequireAuth><TraderDashboard /></RequireAuth>} />
          <Route path="/pnl" element={<RequireAuth redirectTo="/signup"><ProfitTrackerPage /></RequireAuth>} /> {/* Legacy alias */}
          {/* ── Admin (self-gated via HTTP Basic login form — NOT RequireAuth) ── */}
          <Route path="/admin" element={<AdminPage />} />
          {/* ── Private owner tool (self-gated HTTP Basic; hidden from nav/sitemap/robots) ── */}
          <Route path="/command-center" element={<CommandCenterPage />} />
          <Route path="/trader-dna" element={<TraderDnaPage />} />
          <Route path="/admin/seo" element={<AdminSeoPage />} />
          <Route path="/admin/monitor" element={<MonitorDashboard />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/best-prop-firms-2026" element={<Navigate to="/best-prop-firms-2026" replace />} />
          <Route path="/blog/:slug" element={<ArticlePage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/quiz" element={<QuizPage />} />
          <Route path="/tools/market-movers" element={<Navigate to="/tools#market-movers" replace />} />
          <Route path="/tools/vix-regime" element={<Navigate to="/tools#vix-gauge" replace />} />
          <Route path="/tools/payout-timeline-estimator" element={<Navigate to="/tools#calculators" replace />} />
          {/* SEO Tier 1 Pages */}
          <Route path="/futures-prop-firm-comparison" element={<ComparisonPage />} />
          <Route path="/best-futures-prop-firms" element={<BestFirmsPage />} />
          <Route path="/easiest-prop-firms-to-pass" element={<EasiestToPassPage />} />
          <Route path="/best-prop-firms-2026" element={<BestPropFirms2026Page />} />
          <Route path="/community-pulse" element={<CommunityPulsePage />} />
          <Route path="/pricing-tracker" element={<Navigate to="/cheapest-futures-prop-firms" replace />} />
          <Route path="/cheapest-futures-prop-firms" element={<CheapestFirmsPage />} />
          <Route path="/true-cost-prop-firm-comparison" element={<Navigate to="/futures-prop-firm-comparison?sort=truecost" replace />} />
          <Route path="/fastest-payout-futures-prop-firms" element={<Navigate to="/prop-firms-fast-payouts" replace />} />
          <Route path="/best-futures-prop-firms-for-beginners" element={<BeginnersPage />} />
          <Route path="/best-prop-firm-for-nq" element={<BestForNQPage />} />
          <Route path="/best-prop-firm-for-es" element={<BestForESPage />} />
          <Route path="/get-funded-fast" element={<GetFundedFastPage />} />
          <Route path="/passing-prop-firm-evaluations" element={<PassingEvaluationsGuidePage />} />
          <Route path="/prop-firm-tax-guide" element={<PropFirmTaxGuidePage />} />
          <Route path="/prop-firm-scaling-plans" element={<ScalingPlansPage />} />
          <Route path="/prop-firm-payout-methods" element={<PayoutMethodsPage />} />
          {/* Programmatic SEO preset pages */}
          <Route path="/trailing-drawdown-prop-firms" element={<FilteredComparisonPage />} />
          <Route path="/static-drawdown-prop-firms" element={<FilteredComparisonPage />} />
          <Route path="/eod-drawdown-prop-firms" element={<FilteredComparisonPage />} />
          <Route path="/lowest-reset-fee-prop-firms" element={<FilteredComparisonPage />} />
          <Route path="/no-consistency-rule-prop-firms" element={<Navigate to="/prop-firms-without-consistency-rule" replace />} />
          <Route path="/news-trading-prop-firms" element={<FilteredComparisonPage />} />
          <Route path="/balance-based-drawdown-firms" element={<FilteredComparisonPage />} />
          <Route path="/hybrid-drawdown-firms" element={<FilteredComparisonPage />} />
          <Route path="/drawdown-types" element={<DrawdownTypesPage />} />
          <Route path="/topstep-alternatives" element={<AlternativesPage />} />
          <Route path="/apex-alternatives" element={<AlternativesPage />} />
          <Route path="/bulenox-alternatives" element={<AlternativesPage />} />
          <Route path="/tradeday-alternatives" element={<AlternativesPage />} />
          <Route path="/elite-trader-funding-alternatives" element={<AlternativesPage />} />
          <Route path="/e8-markets-alternatives" element={<AlternativesPage />} />
          <Route path="/blusky-alternatives" element={<AlternativesPage />} />
          <Route path="/blue-guardian-alternatives" element={<AlternativesPage />} />
          <Route path="/aquafutures-alternatives" element={<AlternativesPage />} />
          <Route path="/yrm-prop-alternatives" element={<AlternativesPage />} />
          <Route path="/alpha-futures-alternatives" element={<AlternativesPage />} />
          <Route path="/top-one-futures-alternatives" element={<AlternativesPage />} />
          <Route path="/best-prop-firms-under-100" element={<BestFirmsUnder100Page />} />
          <Route path="/prop-firms-without-consistency-rule" element={<PropFirmsWithoutConsistencyPage />} />
          <Route path="/prop-firms-with-no-daily-loss-limit" element={<NoDailyLossPage />} />
          <Route path="/prop-firms-with-no-time-limit" element={<NoTimeLimitPage />} />
          <Route path="/prop-firms-without-evaluation-phase" element={<WithoutEvaluationPage />} />
          <Route path="/prop-firms-with-instant-funding" element={<InstantFundingPage />} />
          <Route path="/best-prop-firms-under-200" element={<BestFirmsUnder200Page />} />
          <Route path="/prop-firms-without-activation-fee" element={<NoActivationFeePage />} />
          <Route path="/prop-firms-fast-payouts" element={<FastPayoutsPage />} />
          <Route path="/topstep-vs-bulenox" element={<TopstepVsBulenoxPage />} />
          <Route path="/topstep-vs-apex" element={<TopstepVsApexPage />} />
          <Route path="/bulenox-vs-tradeday" element={<BulenoxVsTradedayPage />} />
          <Route path="/e8-markets-vs-blue-guardian" element={<E8MarketsVsBlueGuardianPage />} />
          <Route path="/blue-guardian-vs-e8-markets" element={<Navigate to="/e8-markets-vs-blue-guardian" replace />} />
          <Route path="/topstep-vs-apex-vs-bulenox" element={<TopstepVsApexVsBulenoxPage />} />
          <Route path="/prop-firms-with-high-profit-split" element={<HighProfitSplitPage />} />
          <Route path="/prop-firms-low-drawdown" element={<LowDrawdownPage />} />
          <Route path="/best-prop-firms-for-small-accounts" element={<SmallAccountsPage />} />
          <Route path="/prop-firms-with-daily-payouts" element={<DailyPayoutsPage />} />
          <Route path="/best-prop-firms-for-scalping" element={<ScalpingPage />} />
          <Route path="/prop-firms-for-scalping" element={<Navigate to="/best-prop-firms-for-scalping" replace />} />
          <Route path="/drawdown-calculator" element={<DrawdownCalculatorPage />} />
          <Route path="/consistency-rule-calculator" element={<ConsistencyCalculatorPage />} />
          <Route path="/prop-firm-cost-calculator" element={<CostCalculatorPage />} />
          <Route path="/prop-firm-profit-calculator" element={<ProfitCalculatorPage />} />
          <Route path="/do-not-trade" element={<DoNotTradeGame />} />
          {/* Legacy ask-slug redirects (historical inbound links) — must be before /ask/:slug */}
          <Route path="/ask/is-blusky-worth-it" element={<Navigate to="/ask/is-blusky-trading-worth-it" replace />} />
          <Route path="/ask/is-top-one-futures-worth-it" element={<Navigate to="/ask/is-top-one-trader-worth-it" replace />} />
          <Route path="/ask/:slug" element={<AskPage />} />
          <Route path="/prop-firm-questions" element={<AskHubPage />} />
          <Route path="/reviews/:slug" element={<ReviewPage />} />
          <Route path="/prop-firm-payout-status" element={<PayoutStatusPage />} />
          <Route path="/bulenox-vs-top-one-futures" element={<Navigate to="/compare/bulenox-vs-top-one-futures" replace />} />
          <Route path="/tradeday-vs-bulenox" element={<Navigate to="/bulenox-vs-tradeday" replace />} />
          <Route path="/tradeday-vs-topstep" element={<Navigate to="/compare/topstep-vs-tradeday" replace />} />
          {/* Phase 8 — drawdown article merge */}
          <Route path="/blog/trailing-vs-eod-drawdown-explained-simply" element={<Navigate to="/blog/trailing-vs-eod-drawdown-which-is-better" replace />} />
          {/* Phase 8 — skill-level blog retarget */}
          <Route path="/blog/best-prop-firm-for-beginners-vs-experienced" element={<Navigate to="/blog/prop-firm-skill-level-decision-guide" replace />} />
          {/* Comparison hub + dynamic engine */}
          <Route path="/prop-firm-comparisons" element={<ComparisonHubPage />} />
          <Route path="/compare/:slugs" element={<DynamicComparisonPage />} />
          <Route path="/top-one-futures-discount" element={<DynamicDiscountPage />} />
          <Route path="/bulenox-discount" element={<DynamicDiscountPage />} />
          <Route path="/blusky-discount" element={<DynamicDiscountPage />} />
          <Route path="/elite-trader-funding-discount" element={<DynamicDiscountPage />} />
          <Route path="/e8-markets-discount" element={<DynamicDiscountPage />} />
          <Route path="/blue-guardian-discount" element={<DynamicDiscountPage />} />
          <Route path="/aquafutures-discount" element={<DynamicDiscountPage />} />
          <Route path="/blueberry-futures-discount" element={<Navigate to="/futures-prop-firm-discounts" replace />} />
          <Route path="/yrm-prop-discount" element={<DynamicDiscountPage />} />
          <Route path="/alpha-futures-discount" element={<DynamicDiscountPage />} />
          <Route path="/tradeday-discount" element={<DynamicDiscountPage />} />
          <Route path="/prop-firm-rules" element={<Navigate to="/futures-prop-firm-rules" replace />} />
          <Route path="/prop-firm-guides" element={<PropFirmGuidesPage />} />
          <Route path="/site-map" element={<SitemapPage />} />
          <Route path="/futures-prop-firm-rules" element={<PropFirmRulesPage />} />
          <Route path="/futures-prop-firm-discounts" element={<FirmDiscountsPage />} />
          {/* Dynamic discount page — catches new firms not in hardcoded routes */}
          <Route path="/:discountSlug" element={<DynamicDiscountPage />} />
        </Routes>
      </Suspense>
      {/* FuryBot Chat Widget */}
      <FuryBot />
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
